import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Line, ComposedChart } from 'recharts';
import { Flame, Hourglass, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cellColor = (p, i) => {
  const score = (p + 1) * (i + 1);
  if (score >= 15) return 'bg-rose-500 text-white';
  if (score >= 8) return 'bg-amber-400 text-amber-950';
  if (score >= 4) return 'bg-yellow-200 dark:bg-yellow-500/40 text-yellow-900 dark:text-yellow-100';
  return 'bg-emerald-200 dark:bg-emerald-500/30 text-emerald-900 dark:text-emerald-100';
};

export const RiskAnalytics = () => {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/risks/analytics/summary`);
      setData(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const ageingData = Object.entries(data.ageing).map(([bucket, count]) => ({ bucket, count }));

  return (
    <div className="space-y-4" data-testid="risk-analytics">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Flame className="h-5 w-5 text-rose-500" /> Risk Analytics</h2>
        <Button variant="ghost" size="sm" onClick={() => setShow(!show)} data-testid="risk-analytics-toggle">{show ? 'Hide' : 'Show'}</Button>
      </div>
      {show && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Heat map */}
          <Card className="!p-0" data-testid="risk-heatmap">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Heat Map (Probability × Impact)</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-1">
                <div className="flex flex-col justify-between py-1 pr-1 text-[10px] text-muted-foreground">
                  <span>P5</span><span>P4</span><span>P3</span><span>P2</span><span>P1</span>
                </div>
                <div className="grid grid-cols-5 gap-1 flex-1">
                  {[4, 3, 2, 1, 0].map(p => [0, 1, 2, 3, 4].map(i => (
                    <div key={`${p}-${i}`} className={`aspect-square rounded-md flex items-center justify-center text-sm font-bold ${cellColor(p, i)}`}
                      title={`P${p + 1} × I${i + 1} = ${(p + 1) * (i + 1)}`} data-testid={`heat-cell-${p + 1}-${i + 1}`}>
                      {data.matrix[p][i] || ''}
                    </div>
                  )))}
                </div>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1 pl-6">
                <span>I1</span><span>I2</span><span>I3</span><span>I4</span><span>I5</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{data.open_count} open · {data.critical_count} critical · {data.high_count} high · avg score {data.avg_score}</p>
            </CardContent>
          </Card>

          {/* Ageing */}
          <Card className="!p-0" data-testid="risk-ageing">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1"><Hourglass className="h-4 w-4" /> Open Risk Ageing (days)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={ageingData} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Open risks" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Trend */}
          <Card className="!p-0" data-testid="risk-trend">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-1"><TrendingUp className="h-4 w-4" /> 6-Month Trend</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={data.trend} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="created" name="Raised" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={14} />
                  <Line dataKey="closed" name="Closed" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
